import { Injectable } from '@angular/core';
import { Subject } from 'rxjs';

export interface ChatNotificationOptions {
  threadId: string;
  senderName: string;
  messagePreview: string;
  propertyAddress?: string;
  propertyId?: string;
  avatar?: string;
  timestamp: Date;
}

export interface NotificationPreferences {
  enabled: boolean;
  sound: boolean;
  showPreview: boolean;
  doNotDisturb: boolean;
  doNotDisturbStart?: string; // HH:MM format
  doNotDisturbEnd?: string; // HH:MM format
}

@Injectable({
  providedIn: 'root'
})
export class NotificationService {
  private readonly STORAGE_KEY = 'vesta_notification_preferences';
  private preferences: NotificationPreferences;
  private isVisible = true;
  private isFocused = true;
  private activeNotifications = new Map<string, Notification>();
  private handledNotifications = new Set<string>(); // Track which notifications have been clicked

  // Observable for notification clicks
  public notificationClicked$ = new Subject<{threadId: string, propertyId?: string}>();

  constructor() {
    this.preferences = this.loadPreferences();
    this.setupVisibilityDetection();
  }

  // ============================================================
  // Permission Management
  // ============================================================

  /**
   * Request notification permission from the user
   */
  async requestPermission(): Promise<NotificationPermission> {
    if (!this.isSupported()) {
      console.warn('Notifications not supported in this browser');
      return 'denied';
    }

    try {
      return await Notification.requestPermission();
    } catch (error) {
      console.error('Error requesting notification permission:', error);
      return 'denied';
    }
  }

  /**
   * Get current notification permission status
   */
  getPermissionStatus(): NotificationPermission {
    if (!this.isSupported()) {
      return 'denied';
    }
    return Notification.permission;
  }

  // ============================================================
  // Notification Display
  // ============================================================

  /**
   * Show a chat notification
   */
  showChatNotification(options: ChatNotificationOptions): void {
    // Simple checks - component should decide when to call this
    if (!this.isSupported()) {
      return;
    }

    if (this.getPermissionStatus() !== 'granted') {
      return;
    }

    if (!this.preferences.enabled) {
      return;
    }

    const title = options.senderName;
    const body = this.preferences.showPreview
      ? this.truncateMessage(options.messagePreview, 100)
      : 'New message received';

    // Make each notification unique by adding timestamp to tag
    // This prevents the browser from auto-closing/replacing notifications
    const uniqueTag = `chat-${options.threadId}-${Date.now()}`;

    const notificationOptions: NotificationOptions = {
      body: body,
      icon: options.avatar || '/assets/icon/favicon.png',
      badge: '/assets/icon/favicon.png',
      tag: uniqueTag,
      requireInteraction: false,
      data: {
        threadId: options.threadId,
        propertyAddress: options.propertyAddress,
        propertyId: options.propertyId
      }
    };

    try {
      const notification = new Notification(title, notificationOptions);

      // Store reference to active notification
      this.activeNotifications.set(uniqueTag, notification);

      // Handle notification click
      notification.onclick = (event) => {
        event.preventDefault();
        // Only handle once per notification using the unique tag
        if (!this.handledNotifications.has(uniqueTag)) {
          this.handledNotifications.add(uniqueTag);
          this.handleNotificationClick(notification, options.threadId, uniqueTag);
        }
      };

      // Clean up reference when notification is closed
      notification.onclose = () => {
        this.activeNotifications.delete(uniqueTag);
        // Clean up the handled notification tracking after a delay
        setTimeout(() => {
          this.handledNotifications.delete(uniqueTag);
        }, 5000);
      };

      notification.onerror = (error) => {
        console.error('Notification error:', error);
      };
    } catch (error) {
      console.error('Error showing notification:', error);
    }
  }

  /**
   * Test notification - useful for debugging
   */
  showTestNotification(): void {
    console.log('[NotificationService] Showing test notification');
    this.showChatNotification({
      threadId: 'test-123',
      senderName: 'Test User',
      messagePreview: 'This is a test notification',
      timestamp: new Date()
    });
  }

  /**
   * Close a notification by tag
   */
  closeNotification(tag: string): void {
    const notification = this.activeNotifications.get(tag);
    if (notification) {
      notification.close();
      this.activeNotifications.delete(tag);
    }
  }

  // ============================================================
  // Preferences Management
  // ============================================================

  /**
   * Check if notifications are enabled
   */
  isEnabled(): boolean {
    return this.preferences.enabled &&
           this.getPermissionStatus() === 'granted';
  }

  /**
   * Enable or disable notifications
   */
  setEnabled(enabled: boolean): void {
    this.preferences.enabled = enabled;
    this.savePreferences();
  }

  /**
   * Get current notification preferences
   */
  getPreferences(): NotificationPreferences {
    return { ...this.preferences };
  }

  /**
   * Update notification preferences
   */
  updatePreferences(prefs: Partial<NotificationPreferences>): void {
    this.preferences = { ...this.preferences, ...prefs };
    this.savePreferences();
  }

  // ============================================================
  // Utility Methods
  // ============================================================

  /**
   * Check if notifications are supported in the browser
   */
  isSupported(): boolean {
    return 'Notification' in window;
  }

  /**
   * Check if document is currently visible
   */
  isDocumentVisible(): boolean {
    return this.isVisible;
  }

  /**
   * Check if document/window is currently focused
   */
  isDocumentFocused(): boolean {
    return this.isFocused && this.isVisible;
  }

  // ============================================================
  // Private Methods
  // ============================================================

  /**
   * Load preferences from localStorage
   */
  private loadPreferences(): NotificationPreferences {
    try {
      const stored = localStorage.getItem(this.STORAGE_KEY);
      if (stored) {
        return JSON.parse(stored);
      }
    } catch (error) {
      console.error('Error loading notification preferences:', error);
    }
    return this.getDefaultPreferences();
  }

  /**
   * Save preferences to localStorage
   */
  private savePreferences(): void {
    try {
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(this.preferences));
    } catch (error) {
      console.error('Error saving notification preferences:', error);
    }
  }

  /**
   * Get default notification preferences
   */
  private getDefaultPreferences(): NotificationPreferences {
    return {
      enabled: true,
      sound: true,
      showPreview: true,
      doNotDisturb: false,
      doNotDisturbStart: '22:00',
      doNotDisturbEnd: '08:00'
    };
  }

  /**
   * Setup document visibility detection
   */
  private setupVisibilityDetection(): void {
    // Page Visibility API
    document.addEventListener('visibilitychange', () => {
      this.isVisible = !document.hidden;
    });

    // Window focus events
    window.addEventListener('focus', () => {
      this.isFocused = true;
    });

    window.addEventListener('blur', () => {
      this.isFocused = false;
    });

    // Initialize state
    this.isVisible = !document.hidden;
    this.isFocused = document.hasFocus();
  }

  /**
   * Truncate message to specified length
   */
  private truncateMessage(message: string, maxLength: number): string {
    if (!message) {
      return '';
    }
    if (message.length <= maxLength) {
      return message;
    }
    return message.substring(0, maxLength - 3) + '...';
  }

  /**
   * Handle notification click event
   */
  private handleNotificationClick(notification: Notification, threadId: string, uniqueTag: string): void {
    console.log('[NotificationService] Handling notification click:', uniqueTag);

    // Focus the browser window
    window.focus();

    // Emit notification click event with data for components to handle
    // The data includes threadId and propertyId for navigation
    this.notificationClicked$.next({
      threadId: threadId,
      propertyId: notification.data?.propertyId
    });

    // Close the notification
    notification.close();
  }
}
